import { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { setProperties } from "@/stores/properties-store/properties-reducer";
import { toaster } from "@/components/shared/atoms/toaster";

export function useProperties(autoLoad = true) {
  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const isLoadingRef = useRef(false);

  const loadProperties = useCallback(async () => {
    if (isLoadingRef.current) return; // Prevent concurrent loads

    isLoadingRef.current = true;
    setIsLoading(true);
    setError(null);

    try {
      const response = await fetch("/api/properties");
      if (!response.ok) {
        throw new Error("Failed to fetch properties");
      }
      const data = await response.json();
      dispatch(setProperties(data));
    } catch (error) {
      console.error("Error fetching properties:", error);
      setError(
        error instanceof Error ? error.message : "Failed to fetch properties",
      );
      toaster.create({
        title: "Error",
        description: "Failed to load properties. Please try again.",
        type: "error",
        duration: 5000,
        closable: true,
      });
    } finally {
      isLoadingRef.current = false;
      setIsLoading(false);
    }
  }, [dispatch]);

  useEffect(() => {
    if (autoLoad) {
      loadProperties();
    }
  }, [autoLoad, loadProperties]);

  return {
    loadProperties,
    isLoading,
    error,
  };
}
